import React, { useState, useEffect } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

export default function DigoxinCalculator({user}) {
  // Biometric Inputs
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [scr, setScr] = useState('');
  const [hasChf, setHasChf] = useState('no');

  // Concomitant Drug & Physiological Multipliers
  const [amiodarone, setAmiodarone] = useState(false);
  const [verapamil, setVerapamil] = useState(false);
  const [quinidine, setQuinidine] = useState(false);
  const [thyroidStatus, setThyroidStatus] = useState('normal');

  // Regimen Dosing Settings
  const [dose, setDose] = useState('125'); // standard initial 125mcg dose
  const [route, setRoute] = useState('oral'); // oral (F=0.7) vs iv (F=1.0)

  // Step 5: Lab Correlation Input
  const [actualLabValue, setActualLabValue] = useState('');
  const [clinicalIndication, setClinicalIndication] = useState('hf');
  const [simDose, setSimDose] = useState('');
  const [simCss, setSimCss] = useState('0');

  // Calculated Outputs State
  const [outputs, setOutputs] = useState({
    ibw: 0,
    dosingWeight: 0,
    crcl: 0,
    baseClearance: 0,
    finalClearance: 0,
    predictedCss: 0
  });

  useEffect(() => {
    const ageNum = parseFloat(age);
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);
    const scrNum = parseFloat(scr);
    const doseNum = parseFloat(dose);

    if (!ageNum || !weightNum || !scrNum) return;

    // 1. Calculate Ideal Body Weight (IBW) if height is provided
    let ibw = 0;
    let dosingWeight = weightNum; // Default to Actual Weight right away 

    if (heightNum && !isNaN(heightNum)) {
      const heightInches = heightNum / 2.54;
      if (gender === 'male') {
        ibw = 50 + 2.3 * (heightInches - 60);
      } else {
        ibw = 45.5 + 2.3 * (heightInches - 60);
      }
      if (ibw < 0) ibw = 0;

      // Rule: If obese/overweight, cap the dosing weight at IBW
      if (weightNum > ibw) {
        dosingWeight = ibw;
      }
    }
    // If heightNum is blank or zero, ibw stays 0, and dosingWeight stays weightNum!

    // 2. Calculate Cockcroft-Gault CrCl (SI Units Variant)
    let crcl = ((140 - ageNum) * dosingWeight / scrNum) * (gender === 'male' ? 1.23 : 1.04);
    if (crcl < 0) crcl = 0;

    // 3. Compute Base Digoxin Clearance (L/hr)
    let baseClearance = 0;
    if (hasChf === 'no') {
      baseClearance = ((0.8 * dosingWeight) + crcl) * 0.06;
    } else {
      baseClearance = ((0.33 * dosingWeight) + (0.9 * crcl)) * 0.06;
    }

    // Apply Hidden Clinical Handbook Correction Modifiers
    let multiplier = 1.0;
    if (amiodarone) multiplier *= 0.5;
    if (verapamil) multiplier *= 0.75;
    if (quinidine) multiplier *= 0.5;
    
    if (thyroidStatus === 'hypo') multiplier *= 0.7;
    if (thyroidStatus === 'hyper') multiplier *= 1.3;

    const finalClearance = baseClearance * multiplier;

    // 4. Predict Steady-State Concentration (Css in mcg/L)
    const F = route === 'oral' ? 0.7 : 1.0;
    const tau = 24; // Standard daily interval anchor
    const predictedCss = finalClearance > 0 ? (F * doseNum) / (finalClearance * tau) : 0;

    // === RE-ENGINEERED STEP 5: DIGOXIN HIGH-PRECISION INDIVIDUAL RATIO CORE ===
    const simDoseNum = parseFloat(simDose) || 0;
    const currentDoseNum = parseFloat(dose) || 0;
    const actualLabNum = parseFloat(actualLabValue) || 0;
    
    let simulatedResult = 0;

    if (simDoseNum > 0 && currentDoseNum > 0 && actualLabNum > 0) {
      // Direct high-precision individual scaling ratio matching the steady-state biology footprint
      simulatedResult = (simDoseNum / currentDoseNum) * actualLabNum;
    } else if (simDoseNum > 0 && finalClearance > 0) {
      // Fallback to Cockcroft-Gault Multiplier clearance if no actual lab level exists yet
      const currentF = route === 'oral' ? 0.7 : 1.0;
      simulatedResult = (currentF * simDoseNum) / (finalClearance * 24);
    }

    // Add const [simCss, setSimCss] = useState(0); at top of function to hold this value
    setSimCss(simulatedResult);

    setOutputs({
      ibw: ibw.toFixed(1),
      dosingWeight: dosingWeight.toFixed(1),
      crcl: crcl.toFixed(1),
      baseClearance: baseClearance.toFixed(2),
      finalClearance: finalClearance.toFixed(2),
      predictedCss: predictedCss.toFixed(2)
    });

  }, [gender, age, weight, height, scr, hasChf, amiodarone, verapamil, quinidine, thyroidStatus, dose, route, actualLabValue, simDose]);

  return (
  <>
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
      <button 
        type="button"
        className="no-print" 
        onClick={() => {
            const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
            const trackingLabel = `${actualUserEmail}-printed-Digoxin`;
            logUserActivityQuietly(trackingLabel); 
            window.print();
        }} 
        style={{
          padding: '10px 20px',
          backgroundColor: '#27ae60', 
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
        }}
      >
        🖨️ Print PDF Report
      </button>
    </div>
    <div style={{ background: '#111416', minHeight: '100vh', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', padding: '20px', boxSizing: 'border-box' }}>
      
      {/* HEADER BAR TITLE */}
      <div style={{ textAlign: 'center', marginBottom: '25px', borderBottom: '2px solid #00d2ff', paddingBottom: '15px' }}>
        <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#00d2ff', fontWeight: '800' }}>DIGOXIN TDM CALCULATOR Workspace</h2>
        <span style={{ fontSize: '0.8rem', color: '#95a5a6', textTransform: 'uppercase' }}>Dose Initiation & Interaction Telemetry Matrix</span>
      </div>

      {/* DETAILED HOSPITAL GUIDELINE HANDBOOK ACCORDION DROP DOWN */}
      <details style={{ background: '#1c2226', border: '1px solid #3d4e5d', borderRadius: '8px', marginBottom: '20px', padding: '12px', boxSizing: 'border-box' }}>
        <summary style={{ fontWeight: 'bold', color: '#00d2ff', cursor: 'pointer', outline: 'none', fontSize: '0.9rem' }}>
          📋 Check Clinical Sampling Protocols (Click to expand)
        </summary>
        <div style={{ marginTop: '12px', fontSize: '0.85rem', color: '#bdc3c7', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '10px', lineHeight: '1.5' }}>
          {/* STEADY STATE ELIMINATION TIMELINES */}
          <strong style={{ color: '#9b59b6' }}>⏳ Clinical Windows to Achieve Steady State Equilibrium (Css):</strong>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginTop: '6px', background: 'rgba(0,0,0,0.15)', padding: '10px', borderRadius: '6px' }}>
            <div>🚀 <strong>With Loading Dose (LD):</strong> 12 – 24 hours</div>
            <div>🐢 <strong>Without Loading Dose:</strong> 3 – 5 days post initiation</div>
            <div>🔄 <strong>Maintenance Dose Adjustment:</strong> 5 – 7 days post change</div>
            <div style={{ color: '#ff7675' }}>⚠️ <strong>End-Stage Renal Failure (ESRF):</strong> 15 – 20 days tracking window</div>
          </div>

          {/* SAMPLING TIME INFORMATION */}
          <strong style={{ color: '#00d2ff' }}>⏱️ Lab Sampling Time:</strong>
          <ul style={{ marginTop: '4px', marginBottom: '12px' }}>
            <li><strong>💊 Oral Route Profile:</strong> Take sample <span style={{ color: '#fff', fontWeight: 'bold' }}>30 minutes before</span> (or just prior to) the next scheduled dose, OR at least <span style={{ color: '#f1c40f', fontWeight: 'bold' }}>6 hours post-dose</span>.</li>
            <li><strong>💉 Intravenous (IV) Route Profile:</strong> Take sample <span style={{ color: '#fff', fontWeight: 'bold' }}>30 minutes before</span> (or just prior to) the next scheduled dose, OR at least <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>4 hours post-dose</span>.</li>
          </ul>
          <strong>🎯 Target Equilibrium Reference Thresholds:</strong>
          <ul>
            <li>❤️ <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Heart Failure (HF Dosing):</span> Target level ranges strictly between <strong>0.5 – 0.9 mcg/L</strong>.</li>
            <li>⚡ <span style={{ color: '#f1c40f', fontWeight: 'bold' }}>Atrial Fibrillation (AF Rate Control):</span> Target level ranges safely between <strong>0.8 – 2.0 mcg/L</strong>.</li>
            <li>🚨 <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>Toxicity Risk Zone:</span> Serum concentrations exceeding <strong>&gt; 2.0 mcg/L</strong> carry critical high-alert arrhythmia and neuro-toxicity risks.</li>
          </ul>
        </div>
      </details>

      {/* MAIN DATA INPUT INTERFACE SPLIT */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        
        {/* LEFT COLUMN: PARAMETER PROFILING */}
        <div style={{ background: '#1c2226', padding: '20px', borderRadius: '10px', border: '1px solid #2c3539', boxSizing: 'border-box' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', display: 'block', marginBottom: '12px', textTransform: 'uppercase' }}>1. Patient Parameters</span>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={labelStyle}>GENDER:</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)} style={inputStyle}>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>AGE (YEARS):</label>
              <input type="number" placeholder="e.g. 65" value={age} onChange={(e) => setAge(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={labelStyle}>ACTUAL WEIGHT (KG):</label>
              <input type="number" placeholder="e.g. 70" value={weight} onChange={(e) => setWeight(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>HEIGHT (CM): [OPTIONAL]</label>
              <input type="number" placeholder="e.g. 170" value={height} onChange={(e) => setHeight(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            <div>
              <label style={labelStyle}>SERUM CREATININE (μMOL/L):</label>
              <input type="number" placeholder="e.g. 95" value={scr} onChange={(e) => setScr(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>HEART FAILURE STATUS (CHF):</label>
              <select value={hasChf} onChange={(e) => setHasChf(e.target.value)} style={{ ...inputStyle, border: hasChf === 'yes' ? '1px solid #f1c40f' : '1px solid #34495e' }}>
                <option value="no">Patient without CHF (AF only)</option>
                <option value="yes">Patient with CHF Present</option>
              </select>
            </div>
          </div>

          {/* STEP 3 CONCOMITANT INTERACTION SWITCH PANEL */}
          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#2ecc71', display: 'block', marginBottom: '10px', textTransform: 'uppercase', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            2. Interaction Modifiers
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '6px', marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={amiodarone} onChange={(e) => setAmiodarone(e.target.checked)} />
              <span>💊 Concurrent <strong>Amiodarone</strong> therapy (Scales CL by 0.5)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={verapamil} onChange={(e) => setVerapamil(e.target.checked)} />
              <span>💊 Concurrent <strong>Verapamil</strong> therapy (Scales CL by 0.75)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={quinidine} onChange={(e) => setQuinidine(e.target.checked)} />
              <span>💊 Concurrent <strong>Quinidine</strong> therapy (Scales CL by 0.5)</span>
            </label>
            <div style={{ marginTop: '5px' }}>
              <label style={labelStyle}>THYROID METABOLIC STATUS:</label>
              <select value={thyroidStatus} onChange={(e) => setThyroidStatus(e.target.value)} style={inputStyle}>
                <option value="normal">Euthyroid (Normal Metabolism)</option>
                <option value="hypo">Hypothyroidism (Slowed CL × 0.7)</option>
                <option value="hyper">Hyperothyroidism (Accelerated CL × 1.3)</option>
              </select>
            </div>
          </div>

          {/* DRUG REGIMEN DOSE SETTING */}
          <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#f1c40f', display: 'block', marginBottom: '10px', textTransform: 'uppercase' }}>
            3. Scheduled Regimen Configuration
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={labelStyle}>MAINTENANCE DOSE (MCG / 24H):</label>
              <select value={dose} onChange={(e) => setDose(e.target.value)} style={inputStyle}>
                <option value="62.5">62.5 mcg Daily (0.0625 mg)</option>
                <option value="125">125 mcg Daily (0.125 mg)</option>
                <option value="187.5">187.5 mcg Daily (0.1875 mg)</option>
                <option value="250">250 mcg Daily (0.25 mg)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>FORMULATION ROUTE:</label>
              <select value={route} onChange={(e) => setRoute(e.target.value)} style={inputStyle}>
                <option value="oral">Oral Tablet (F = 0.7)</option>
                <option value="iv">Intravenous Injection (F = 1.0)</option>
              </select>
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: INTERACTIVE TELEMETRY OUTPUT MATRICES */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          
          {/* STEP 1-3 PHARMACOKINETIC WEIGHT & CLEARANCE PANEL */}
          <div style={{ background: '#1c2226', padding: '15px', borderRadius: '10px', border: '1px solid #2c3539', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#95a5a6', textTransform: 'uppercase', display: 'block', marginBottom: '10px' }}>
              📊 Pharmacokinetic Baseline Tracking
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.85rem' }}>
                {/* IBW BOX */}
                <div style={statBoxStyle}>
                    <span style={{ color: '#95a5a6', fontSize: '0.7rem', fontWeight: 'bold' }}>1. IDEAL BODY WEIGHT (IBW):</span>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff', margin: '2px 0' }}>
                        {height ? `${outputs.ibw} kg` : 'N/A (Height not entered)'}
                    </div>
                    <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontStyle: 'italic', lineHeight: '1.2' }}>
                        Formula: Male = 50 + 2.3 × (Ht in inches - 60) | Female = 45.5 + 2.3 × (Ht in inches - 60)
                    </span>
                    <span style={{ fontSize: '0.65rem', color: '#00d2ff', marginTop: '3px' }}>
                        Applied Dosing Weight Baseline: <strong>{outputs.dosingWeight} kg</strong> {height && parseFloat(weight) > parseFloat(outputs.ibw) ? "(Capped at IBW due to Obesity)" : "(Actual Weight)"}
                    </span>
                </div>
                {/* CrCl BOX */}
                <div style={statBoxStyle}>
                    <span style={{ color: '#3498db', fontSize: '0.7rem', fontWeight: 'bold' }}>2. ESTIMATED CREATININE CLEARANCE (CrCL):</span>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#3498db', margin: '2px 0' }}>
                        {outputs.crcl} mL/min
                    </div>
                    <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontStyle: 'italic', lineHeight: '1.2' }}>
                        Formula (Cockcroft-Gault SI): [((140 - Age) × Weight) / Scr (μmol/L)] × [1.23 (M) or 1.04 (F)]
                    </span>
                </div>
                {/* CL DIGOXIN BOX */}
                <div style={statBoxStyle}>
                    <span style={{ color: '#2ecc71', fontSize: '0.7rem', fontWeight: 'bold' }}>3. TOTAL COMPENSATED DIGOXIN CLEARANCE (CL):</span>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2ecc71', margin: '2px 0' }}>
                    {outputs.finalClearance} L/hr
                    </div>
                    <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontStyle: 'italic', marginBottom: '4px', lineHeight: '1.2' }}>
                    {hasChf === 'yes' 
                        ? "Formula (With CHF): [((0.33 × BW) + (0.9 × CrCl)) × 0.06] × Interaction Modifiers" 
                        : "Formula (Without CHF): [((0.8 × BW) + CrCl) × 0.06] × Interaction Modifiers"
                    }
                    </span>
                    <div style={{ fontSize: '0.65rem', color: '#bdc3c7', background: 'rgba(255,255,255,0.03)', padding: '4px', borderRadius: '4px' }}>
                    Base CL: {outputs.baseClearance} L/hr | Modifiers Active: {[
                        amiodarone && "Amiodarone(0.5)",
                        verapamil && "Verapamil(0.75)",
                        quinidine && "Quinidine(0.5)",
                        thyroidStatus === 'hypo' && "Hypothyroid(0.7)",
                        thyroidStatus === 'hyper' && "Hyperthyroid(1.3)"
                    ].filter(Boolean).join(', ') || "None (1.0)"}
                    </div>
                </div>

            </div>
          </div>

          {/* STEP 4: PREDICTED CSS STEADY-STATE ENGINE */}
          <div style={{ background: '#1c2226', padding: '15px', borderRadius: '10px', border: '1px solid #3d4e5d', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '2px' }}>
              🔮 4. Predicted Steady State Level (Css Estimation)
            </span>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fff', margin: '2px 0' }}>
              {outputs.predictedCss} <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#bdc3c7' }}>mcg/L (ng/mL)</span>
            </div>
            <div style={{ fontSize: '0.65rem', color: '#7f8c8d', fontStyle: 'italic', lineHeight: '1.2', marginTop: '4px' }}>
              Formula: Css = (Bioavailability (F) × Daily Dose) / (Final Digoxin CL × 24 Hours)
            </div>
            <div style={{ fontSize: '0.65rem', color: '#a0a0a0', marginTop: '3px' }}>
              Applied Bioavailability Factor (F): <strong>{route === 'oral' ? "0.7 (Oral Tablet)" : "1.0 (Intravenous IV)"}</strong>
            </div>
          </div>
          {/* STEP 5: CLINICAL LAB CORRELATION & DISCRETE TITRATION SIMULATOR */}
          <div style={{ background: '#1c2226', padding: '20px', borderRadius: '10px', border: '1px solid #2c3539', boxSizing: 'border-box' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#9b59b6', display: 'block', marginBottom: '12px', textTransform: 'uppercase' }}>
              🧪 5. Clinical Assay Interpretation & Titration Simulator
            </span>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={labelStyle}>TARGET THERAPEUTIC INDICATION:</label>
                <select value={clinicalIndication} onChange={(e) => setClinicalIndication(e.target.value)} style={inputStyle}>
                  <option value="hf">Heart Failure Baseline (0.5 - 0.9 mcg/L)</option>
                  <option value="af">Atrial Fibrillation Baseline (0.8 - 2.0 mcg/L)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>PATIENT ACTUAL LAB ASSAY VALUE (MCG/L):</label>
                <input 
                  type="number" 
                  step="0.1" 
                  placeholder="Type reported serum level" 
                  value={actualLabValue} 
                  onChange={(e) => setActualLabValue(e.target.value)} 
                  style={{ ...inputStyle, border: actualLabValue ? '1px solid #9b59b6' : '1px solid #34495e' }} 
                />
              </div>
            </div>

            {/* NEW INTERACTIVE SANDBOX INPUT PANEL */}
            {actualLabValue && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '15px', marginBottom: '15px', alignItems: 'center' }}>
                <div>
                  <label style={labelStyle}>PROPOSED NEW MAINTENANCE DOSE (MCG/DAY):</label>
                  <select value={simDose} onChange={(e) => setSimDose(e.target.value)} style={inputStyle}>
                    <option value="">-- Select Available Tablet Size --</option>
                    <option value="62.5">62.5 mcg/day (0.0625 mg)</option>
                    <option value="125">125 mcg/day (0.125 mg)</option>
                    <option value="187.5">187.5 mcg/day (Alternating 125/250 mcg)</option>
                    <option value="250">250 mcg/day (0.25 mg)</option>
                  </select>
                </div>
                <div style={{ background: '#111416', padding: '12px', borderRadius: '6px', textAlign: 'center', border: '1px solid #2c3539' }}>
                  <span style={{ fontSize: '0.72rem', color: '#95a5a6', fontWeight: 'bold' }}>PROJECTED CSS TARGET:</span>
                  <strong style={{ display: 'block', fontSize: '1.4rem', color: '#00d2ff', margin: '2px 0' }}>
                    {simDose ? parseFloat(simCss).toFixed(2) : '--'} <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#95a5a6' }}>mcg/L</span>
                  </strong>
                </div>
              </div>
            )}

            {/* VISUAL SPECTRUM PROGRESS SLIDER TRACK BAR */}
            {simDose && actualLabValue && (() => {
                let lowTarget = clinicalIndication === 'hf' ? 0.5 : 0.8;
                let highTarget = clinicalIndication === 'hf' ? 0.9 : 2.0;
                
                // Absolute chart horizontal landscape cap at 2.5 mcg/L
                const leftPercent = (lowTarget / 2.5) * 100;
                const widthPercent = ((highTarget - lowTarget) / 2.5) * 100;
                const pinPercent = (parseFloat(simCss) / 2.5) * 100;

                return (
                  <div style={{ padding: '12px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginBottom: '15px' }}>
                    <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '15px 0 15px 0', overflow: 'visible' }}>
                      
                      {/* GREEN TARGET SPEC ZONE */}
                      <div style={{ 
                        position: 'absolute', 
                        left: `${leftPercent}%`, 
                        width: `${widthPercent}%`, 
                        height: '100%', 
                        background: 'rgba(46, 204, 113, 0.22)', 
                        borderLeft: '1px dashed #2ecc71', 
                        borderRight: '1px dashed #2ecc71',
                        borderRadius: '4px'
                      }} />

                      {/* GLOWING PULSING INDICATOR NODE */}
                      <div style={{
                        position: 'absolute',
                        left: `${Math.max(0, Math.min(98, pinPercent))}%`,
                        transform: 'translateX(-50%)',
                        width: '18px',
                        height: '18px',
                        top: '-2px',
                        background: '#f1c40f',
                        borderRadius: '50%',
                        boxShadow: '0 0 12px #f1c40f',
                        border: '2px solid #fff',
                        transition: 'left 0.2s ease-out',
                        zIndex: 10
                      }} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '5px' }}>
                      <span>0 mcg/L</span>
                      <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Indication Target Region: {lowTarget} - {highTarget} mcg/L</span>
                      <span>2.5 mcg/L</span>
                    </div>

                    {/* HIGH-PRECISION LINEAR CALCULATION WORKSHEET PROOF PROOF FOOTER */}
                    <div style={{ fontSize: '0.65rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', marginTop: '5px', textAlign: 'left', lineHeight: '1.4' }}>
                      Formula Predict Css: (Proposed New Dose / Current Active Dose) × Actual Measured Lab Level<br />
                      Individual Patient Linear Ratio Trace: ({simDose} mcg / {dose} mcg) × {actualLabValue} mcg/L = {parseFloat(simCss).toFixed(2)} mcg/L
                    </div>
                  </div>
                );
            })()}

            {/* DYNAMIC HANDBOOK WORKSHEET INTERPRETATION BANNER */}
            {actualLabValue && (() => {
              const val = parseFloat(actualLabValue);
              let lowTarget = clinicalIndication === 'hf' ? 0.5 : 0.8;
              let highTarget = clinicalIndication === 'hf' ? 0.9 : 2.0;
              
              if (val > 2.0) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ background: 'rgba(231, 76, 60, 0.15)', border: '1px solid #e74c3c', color: '#ff7675', padding: '12px', borderRadius: '6px', fontSize: '0.82rem' }}>
                      <strong>🚨 RESULT STATUS: POTENTIAL TOXIC / TOXIC CORRELATION (&gt;2.0 mcg/L)</strong>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '0.78rem' }}>
                      <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #e74c3c' }}>
                        <span style={{ color: '#e74c3c', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>⚠️ Contributing Risk Factors:</span>
                        • Overdosage / Dosing calculation mismatch<br />
                        • Renal Failure / Acute Kidney Injury (AKI)<br />
                        • Hypokalemia / Hypomagnesemia<br />
                        • CHF Exacerbation<br />
                        • P-gp Inhibitors (Amiodarone, Verapamil, etc.)
                      </div>
                      <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #f1c40f' }}>
                        <span style={{ color: '#f1c40f', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>🤢 Monitor Toxic Side Effects:</span>
                        • Gastrointestinal: Nausea, Vomiting<br />
                        • Electrolyte Surges: Hyperkalemia<br />
                        • Cardiac: Sinus Bradycardia, Ventricular Arrhythmias<br />
                        • Neurological: General Weakness, Yellow-Green Vision Halos
                      </div>
                    </div>

                    <div style={{ background: 'rgba(155, 89, 182, 0.1)', border: '1px solid #9b59b6', color: '#d6a2e8', padding: '12px', borderRadius: '6px', fontSize: '0.8rem', lineHeight: '1.4' }}>
                      <strong>📋 CLINICAL RECOMMENDATION:</strong><br />
                      Withhold Digoxin treatment immediately. Continuously monitor ECG telemetry lines and track clinical signs/symptoms of toxicity. Treat electrolytes or administer digoxin-specific Fab fragments if indicated, then readjust maintenance doses down significantly after clearance normalization.
                    </div>
                  </div>
                );
              } 
              
              else if (val >= lowTarget && val <= highTarget) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ background: 'rgba(46, 204, 113, 0.15)', border: '1px solid #2ecc71', color: '#2ecc71', padding: '12px', borderRadius: '6px', fontSize: '0.82rem' }}>
                      <strong>✅ RESULT STATUS: WITHIN INDICATION THERAPEUTIC RANGE ({lowTarget} – {highTarget} mcg/L)</strong>
                    </div>
                    
                    <div style={{ background: '#111416', padding: '12px', borderRadius: '6px', fontSize: '0.78rem', borderLeft: '3px solid #2ecc71', lineHeight: '1.4' }}>
                      <strong>📋 CLINICAL STRATEGY EVALUATION:</strong><br />
                      • <strong>Good Response Visualized:</strong> Maintain current scheduled maintenance dose regime baseline. Resample only upon clinical modification or altered renal tracks.<br />
                      • <strong>Poor Response Visualized:</strong> If therapeutic goals are unmet despite ideal target concentrations, evaluate alternative physiological factors contributing to poor systemic response and treat accordingly.
                    </div>
                  </div>
                );
              } 
              
              else if (val < lowTarget) {
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ background: 'rgba(241, 196, 15, 0.15)', border: '1px solid #f1c40f', color: '#f1c40f', padding: '12px', borderRadius: '6px', fontSize: '0.82rem' }}>
                      <strong>⚠️ RESULT STATUS: SUBTHERAPEUTIC DEFICIT (&lt; {lowTarget} mcg/L)</strong>
                    </div>

                    <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', fontSize: '0.78rem', borderLeft: '3px solid #3498db' }}>
                      <span style={{ color: '#3498db', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>🔍 Investigational Root Causes:</span>
                      • Sub-optimal compliance patterns<br />
                      • Inadequate dose titration framework<br />
                      • <strong>Incorrect Sampling Timeline:</strong> Drawn too late, or before true steady state accumulation equilibrium (7-14 days)<br />
                      • Accelerator Interactions (Hyperthyroidism, Rifampicin, Phenytoin)
                    </div>

                    <div style={{ background: 'rgba(52, 152, 219, 0.1)', border: '1px solid #3498db', color: '#a5d8ff', padding: '12px', borderRadius: '6px', fontSize: '0.8rem', lineHeight: '1.4' }}>
                      <strong>📋 CLINICAL RECOMMENDATION:</strong><br />
                      Assess compliance metrics and verify exact lab sample extraction timelines. If compliance histories and sampling profiles are fully satisfactory, utilize the tablet simulation drop-down to determine a safer maintenance size upward adjustment, and re-schedule a verification assay.
                    </div>
                  </div>
                );
              }
              
              else {
                return (
                  <div style={{ background: 'rgba(230, 126, 34, 0.15)', border: '1px solid #e67e22', color: '#f39c12', padding: '12px', borderRadius: '6px', fontSize: '0.82rem', lineHeight: '1.4' }}>
                    <strong>⚠️ RESULT STATUS: ABOVE DESIGNATED INDICATION MAXIMUM CAP</strong><br />
                    Concentration value ({val} mcg/L) exceeds optimal targets for your selected profile, but remains below the absolute high alert toxicity barrier (2.0 mcg/L). Check for rising Serum Creatinine trends, check potassium baselines, and monitor for early signs of gastrointestinal distress.
                  </div>
                );
              }
            })()}

            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}     
            <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
              *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
            </p>
          </div>
        </div>

      </div>

    </div>
  </>
  );
}

// Minimalist, Responsive Dashboard Inline Styles
const labelStyle = {
  fontSize: '0.68rem',
  fontWeight: 'bold',
  color: '#95a5a6',
  display: 'block',
  marginBottom: '4px',
  letterSpacing: '0.3px'
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: '#2c3e50',
  border: '1px solid #34495e',
  color: '#fff',
  borderRadius: '6px',
  fontSize: '0.82rem',
  outline: 'none',
  boxSizing: 'border-box'
};

const statBoxStyle = {
  background: '#111416',
  padding: '10px',
  borderRadius: '6px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
};